/**
 * Settings › Notifications — who is told about what, on which channels.
 * Wire shapes for /notifications/event-types and /notifications/subscriptions.
 */

import { z } from "zod";

export const channelSchema = z.enum(["inapp", "email", "webhook", "slack", "pagerduty", "sms"]);
export type Channel = z.infer<typeof channelSchema>;

export const severitySchema = z.enum(["info", "warning", "critical"]);

export const eventTypeSchema = z.object({
  id: z.string(),
  description: z.string(),
  severity: severitySchema,
  category: z.string(),
  default_channels: z.array(channelSchema),
});
export type EventType = z.infer<typeof eventTypeSchema>;
export const eventTypesResponseSchema = z.object({ data: z.array(eventTypeSchema) });

export const recipientKindSchema = z.enum(["admin", "user", "webhook", "email_external", "role"]);
export type RecipientKind = z.infer<typeof recipientKindSchema>;

export const subscriptionSchema = z.object({
  id: z.string().uuid(),
  event_type_pattern: z.string(),
  recipient: z.object({
    kind: recipientKindSchema,
    admin_id: z.string().uuid().nullable().optional(),
    user_id: z.string().uuid().nullable().optional(),
    webhook_id: z.string().uuid().nullable().optional(),
    email: z.string().optional().default(""),
    role: z.string().optional().default(""),
  }),
  channels: z.array(channelSchema),
  severity_floor: z.union([severitySchema, z.literal("")]).default(""),
  enabled: z.boolean(),
  created_at: z.string(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;
export const subscriptionsResponseSchema = z.object({ data: z.array(subscriptionSchema) });

/** The channels this deployment delivers today. */
export const DELIVERABLE_CHANNELS: readonly Channel[] = ["inapp", "email"] as const;

export const newSubscriptionSchema = z
  .object({
    event_type_pattern: z.string().min(1, "Choose an event"),
    recipient_kind: z.enum(["role", "admin", "email_external"]),
    recipient_role: z.string().optional(),
    recipient_admin_id: z.string().optional(),
    recipient_email: z.string().optional(),
    channels: z.array(channelSchema).min(1, "Choose at least one channel"),
    severity_floor: z.union([severitySchema, z.literal("")]),
  })
  .superRefine((v, ctx) => {
    if (v.recipient_kind === "role" && !["tenant_admin", "tenant_operator"].includes(v.recipient_role ?? "")) {
      ctx.addIssue({ code: "custom", path: ["recipient_role"], message: "Choose a role" });
    }
    if (v.recipient_kind === "admin" && !z.uuid().safeParse(v.recipient_admin_id).success) {
      ctx.addIssue({ code: "custom", path: ["recipient_admin_id"], message: "Choose an administrator" });
    }
    if (v.recipient_kind === "email_external" && !z.email().safeParse(v.recipient_email?.trim()).success) {
      ctx.addIssue({ code: "custom", path: ["recipient_email"], message: "Enter an email address" });
    }
    if (v.channels.some((c) => !DELIVERABLE_CHANNELS.includes(c) || (v.recipient_kind === "email_external" && c !== "email"))) {
      ctx.addIssue({ code: "custom", path: ["channels"], message: "Choose channels supported by this recipient" });
    }
  });
export type NewSubscription = z.infer<typeof newSubscriptionSchema>;
